import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface KPICardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  description?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant?: 'default' | 'success' | 'warning' | 'danger';
  loading?: boolean;
  onClick?: () => void;
}

// ─────────────────────────────────────────────────────────────
// Variant Styles
// ─────────────────────────────────────────────────────────────

const variantStyles = {
  default: {
    iconBg: 'bg-brand-lagune-100',
    iconColor: 'text-brand-lagune-600',
    ring: 'hover:ring-brand-lagune-200',
  },
  success: {
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    ring: 'hover:ring-emerald-200',
  },
  warning: {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    ring: 'hover:ring-amber-200',
  },
  danger: {
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    ring: 'hover:ring-red-200',
  },
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export function KPICard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  variant = 'default',
  loading = false,
  onClick,
}: KPICardProps) {
  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        'bg-white rounded-xl border border-slate-200 p-6 shadow-sm transition-all duration-200',
        'hover:shadow-md hover:ring-2',
        styles.ring,
        onClick && 'cursor-pointer'
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          {loading ? (
            <div className="mt-2 h-8 w-20 animate-pulse rounded bg-slate-200" />
          ) : (
            <p className="mt-2 text-3xl font-bold text-slate-900">
              {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
            </p>
          )}
          {description && (
            <p className="mt-1 text-xs text-slate-500">{description}</p>
          )}
          {trend && (
            <div className="mt-2 flex items-center gap-1">
              <span
                className={cn(
                  'text-xs font-medium',
                  trend.isPositive ? 'text-emerald-700' : 'text-red-600'
                )}
              >
                {trend.isPositive ? '+' : ''}
                {trend.value}%
              </span>
              <span className="text-xs text-slate-500">vs mois dernier</span>
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex h-12 w-12 items-center justify-center rounded-xl',
            styles.iconBg
          )}
        >
          <Icon className={cn('h-6 w-6', styles.iconColor)} />
        </div>
      </div>
    </div>
  );
}

export default KPICard;
